import { useCallback, useEffect, useState } from 'react';

/**
 * 화면 여러 개가 같은 서버 응답을 쓰는 경우를 위한 최소 캐시 + 훅.
 *
 * 이 프로젝트에 react-query 류 서버상태 라이브러리가 없어서, 화면마다 직접 호출하면
 * 같은 요청이 여러 번 나가고 화면 간 값이 어긋난다(디지털트윈 화면 1·2·5 가 그런 경우).
 * 필요한 만큼만 구현한 대체물이다.
 */

// ── 사용자에게 보여줄 오류와 내부 오류 구분 ────────────────────

/**
 * 사용자에게 그대로 노출해도 되는 오류를 표시하는 마커.
 *
 * 이게 필요한 이유: 공용 인터셉터(`utils/api.ts`)의 거부 핸들러가
 * `const {response:{status}} = error;` 로 시작해서, 응답이 없는 실패(타임아웃·오프라인·CORS)에서는
 * 원본 에러 대신 영문 TypeError 를 던진다. 그 `message` 를 그대로 화면에 쓰면
 * "Cannot destructure property 'status' of ..." 같은 내부 문구가 사용자에게 노출된다.
 *
 * instanceof 대신 플래그를 쓰는 이유: Error 서브클래스는 ES5 로 낮춰 트랜스파일되면
 * instanceof 가 깨진다. 플래그는 트랜스파일 결과와 무관하게 동작한다.
 */
type UserFacingFlag = { __userFacing: true };

export const userFacingError = (message: string): Error & UserFacingFlag =>
  Object.assign(new Error(message), { __userFacing: true as const });

/**
 * 화면에 표시할 문구를 고른다.
 * 우리가 만든 한국어 오류만 노출하고, 그 외(인터셉터 TypeError 등)는 폴백으로 대체한다.
 * 원본은 콘솔에만 남겨 디버깅 가능성을 유지한다.
 */
export const userMessage = (err: any, fallback: string): string => {
  if (err && err.__userFacing === true && typeof err.message === 'string' && err.message !== '') {
    return err.message;
  }
  console.error('[DigitalTwin] 처리되지 않은 오류:', err);
  return fallback;
};

// ── 캐시 ──────────────────────────────────────────────────────

export type CachedResource<T> = {
  /** force=true 면 캐시를 무시하고 다시 조회한다 */
  get: (force?: boolean) => Promise<T>;
  /** 원본 데이터가 바뀌는 동작(예: 문진 저장) 뒤에 호출한다 */
  invalidate: () => void;
};

/**
 * @param fetcher 실제 조회
 * @param accountKey 이 응답이 어느 계정 것인지 나타내는 값(예: familyId).
 *   호출 시점마다 평가해 캐시된 값의 키와 다르면 캐시를 버린다.
 *
 *   이 인자가 있어야 하는 이유: 로그아웃·계정 전환이 soft navigation(`navigate('/')`)이라
 *   페이지 리로드가 없고 모듈 스코프가 유지된다. 캐시가 계정을 구분하지 않으면
 *   B 계정으로 로그인해도 A 계정의 건강 데이터가 그대로 렌더된다.
 *   invalidate 를 로그아웃 지점마다 호출하는 방식은 한 곳만 빠뜨려도 같은 사고가 나므로,
 *   키 비교로 구조적으로 막는다.
 */
export const createCachedResource = <T,>(
  fetcher: () => Promise<T>,
  accountKey: () => string,
): CachedResource<T> => {
  let cached: Promise<T> | null = null;
  let cachedKey: string | null = null;

  const drop = () => {
    cached = null;
    cachedKey = null;
  };

  return {
    get: (force = false) => {
      const key = accountKey();
      if (force || cached === null || cachedKey !== key) {
        cachedKey = key;
        cached = fetcher().catch((err) => {
          // 실패는 캐시하지 않는다 — 다시 시도할 수 있어야 한다
          drop();
          throw err;
        });
      }
      return cached;
    },
    invalidate: drop,
  };
};

// ── 훅 ────────────────────────────────────────────────────────

export type UseResource<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** 캐시를 버리고 다시 조회 */
  reload: () => void;
};

export const useCachedResource = <T,>(
  resource: CachedResource<T>,
  fallbackMessage: string,
): UseResource<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    (force: boolean) => {
      let alive = true;
      setLoading(true);
      setError(null);
      resource
        .get(force)
        .then((d) => {
          if (alive) setData(d);
        })
        .catch((err: any) => {
          if (!alive) return;
          // 실패 시 이전 값을 남기지 않는다 — 오래된 결과를 현재로 오인하면 안 된다
          setData(null);
          setError(userMessage(err, fallbackMessage));
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
      return () => {
        alive = false;
      };
    },
    [resource, fallbackMessage],
  );

  useEffect(() => run(false), [run]);

  const reload = useCallback(() => {
    resource.invalidate();
    run(true);
  }, [resource, run]);

  return { data, loading, error, reload };
};
